import React from "react";
import { Layers, ArrowRight, Info } from "lucide-react";
import useResearchFlags from "../../hooks/useResearchFlags";
import { getArchetype } from "./archetypeMap";

/**
 * ArchetypeCard
 *
 * Renders on the Risk Assessment results page (CryptoReportView). Shows
 * the user's risk profile, the illustrative model archetype for that
 * profile (sleeve mix + within-stocks risk-character split), and a
 * single button that deep-links to the Research tab with Module A
 * pre-filtered to the user's primary risk-character bucket.
 *
 * COMPLIANCE — absolutely critical:
 *   - This card never names specific tickers.
 *   - It's clearly labeled "Illustrative · Not personalized · Not a
 *     recommendation."
 *   - The deep-link goes to Research as a starting lens; it does not
 *     auto-execute or auto-select anything.
 *
 * Feature-flag gated by `archetypeViewEnabled` — if the flag is off,
 * this component renders nothing.
 */

// Sleeve color tokens — one per asset leg. Stock Signals is purple
// to visually separate it from Equities (sky blue) since both are
// stock-based but represent very different strategies (passive picks
// vs. active subscription).
const SLEEVE_COLORS = {
  crypto:       { fill: "bg-amber-500/80",  label: "Crypto" },
  equities:     { fill: "bg-sky-500/80",    label: "Equities" },
  stockSignals: { fill: "bg-purple-500/80", label: "Stock Signals" },
  cash:         { fill: "bg-arc-line",      label: "Cash / Stables" },
};

const STOCK_BUCKET_COLORS = {
  Conservative: { fill: "bg-emerald-500/80", text: "text-emerald-300" },
  Moderate:     { fill: "bg-amber-500/80",   text: "text-amber-300" },
  Aggressive:   { fill: "bg-red-500/80",     text: "text-red-300" },
};

/**
 * Trigger a tab switch (and pass an optional pending Research filter)
 * via a window-level custom event. FinancialDashboard listens for it.
 *
 * Using a custom event here instead of prop-drilling because the
 * Risk Assessment chain (FinancialDashboard → ProfileTab →
 * RiskAssessmentComponent → CryptoReportView → ArchetypeCard) would
 * otherwise need a callback threaded through every layer for this one
 * interaction.
 */
const navigateTo = (tab, researchFilter) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("arc:navigate", {
      detail: { tab, researchFilter },
    })
  );
};

/* ─── Stacked-bar primitive ──────────────────────────────────────── */

const StackedBar = ({ segments }) => (
  <div className="w-full h-3 rounded-full overflow-hidden flex bg-arc-line">
    {segments.map((seg, i) => (
      <div
        key={i}
        className={seg.fill}
        style={{ width: `${seg.value}%` }}
        title={`${seg.label} ${seg.value}%`}
      />
    ))}
  </div>
);

/* ─── Main component ─────────────────────────────────────────────── */

const ArchetypeCard = ({ profile }) => {
  const { flags } = useResearchFlags();

  // Gated by feature flag — render nothing when off.
  if (!flags.archetypeViewEnabled) return null;
  if (!profile) return null;

  const archetype = getArchetype(profile);

  const sleeveSegments = ["crypto", "equities", "stockSignals", "cash"].map((k) => ({
    label: SLEEVE_COLORS[k].label,
    fill: SLEEVE_COLORS[k].fill,
    value: archetype.sleeve[k],
  }));

  const stockSegments = ["Conservative", "Moderate", "Aggressive"].map((k) => ({
    label: k,
    fill: STOCK_BUCKET_COLORS[k].fill,
    value: archetype.withinStocks[k],
  }));

  return (
    <div className="rounded-2xl border-2 border-arc-gold/40 bg-gradient-to-br from-arc-gold/10 via-arc-gold/5 to-transparent p-5 sm:p-6 mt-6">
      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-arc-gold/15 border border-arc-gold/30 flex items-center justify-center flex-shrink-0">
          <Layers size={16} className="text-arc-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-arc-gold mb-0.5">
            Model archetype for your profile
          </p>
          <h3 className="font-display text-lg sm:text-xl font-bold text-arc-ink">
            Illustrative structure for a {profile} profile
          </h3>
        </div>
      </div>

      {/* Compliance label */}
      <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 mb-5">
        <Info size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] sm:text-xs text-amber-200 leading-relaxed">
          <span className="font-semibold">
            Illustrative · Not personalized · Not a recommendation.
          </span>{" "}
          This is a teaching example of how a portfolio with your risk profile
          might be structured. It does not name specific securities, does not
          account for your full financial picture, and is not advice.
        </p>
      </div>

      {/* Sleeve mix — equal-thirds across the 3 growth legs within each
          profile. Cash reserve scales with risk tolerance. Further
          differentiation lives in the within-stocks split below. */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <p className="eyebrow">Sleeve mix</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
            Equal-thirds growth
          </p>
        </div>
        <p className="text-[11px] text-arc-dim leading-relaxed mb-3">
          Your deployed capital splits equally across the three growth
          legs — Crypto, Equities, and Stock Signals. The cash reserve
          scales with your risk profile (conservatives keep more dry
          powder; aggressive profiles deploy more).
        </p>
        <StackedBar segments={sleeveSegments} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {sleeveSegments.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-2 rounded-lg border border-arc-line bg-arc-surface p-2"
            >
              <span className={`w-2.5 h-2.5 rounded-full ${s.fill}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-arc-dim leading-none">
                  {s.label}
                </p>
                <p className="num text-sm font-bold text-arc-ink">
                  {s.value}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Within-stocks split — applies to both Equities AND Stock
          Signals sleeves since both end up as stock positions. */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <p className="eyebrow">Within your stock positions</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
            Risk-character mix
          </p>
        </div>
        <StackedBar segments={stockSegments} />
        <div className="grid grid-cols-3 gap-2 mt-3">
          {stockSegments.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-2 rounded-lg border border-arc-line bg-arc-surface p-2"
            >
              <span className={`w-2.5 h-2.5 rounded-full ${s.fill}`} />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-[10px] leading-none ${STOCK_BUCKET_COLORS[s.label].text}`}
                >
                  {s.label}
                </p>
                <p className="num text-sm font-bold text-arc-ink">
                  {s.value}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deep-link CTA */}
      <button
        type="button"
        onClick={() =>
          navigateTo("research", {
            riskCharacter: archetype.primaryStocksBucket,
          })
        }
        className="btn-tactile btn-gold w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold"
      >
        Explore {archetype.primaryStocksBucket} stocks in Research
        <ArrowRight size={14} strokeWidth={2.5} />
      </button>

      <p className="text-[11px] text-arc-dim mt-2.5 text-center">
        Opens the Analyst Ratings module pre-filtered to your primary risk
        character. You choose what to research from there.
      </p>
    </div>
  );
};

export default ArchetypeCard;
